
import React, { useState, useRef } from 'react';
import './ProfilePage.css';
import { useApp } from '../../context/AppContext';
import Avatar from '../../components/Avatar/Avatar';
import { toast } from '../../components/Toast/Toast';

interface ProfilePageProps {
  onSignOut?: () => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onSignOut }) => {
  const { currentUser, orders, products, posts, updateProfile, updateAvatar, subscriptionTiers, addShippingAddress } = useApp();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ name: '', street: '', city: '', state: '', zip: '', country: 'USA', isDefault: false });

  const userOrders = orders.filter(o => o.userId === currentUser.id);
  const savedPosts = posts.filter(p => p.savedBy.includes(currentUser.id));
  const wishlistProducts = products.filter(p => currentUser.wishlist.includes(p.id));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'status-delivered';
      case 'shipped': return 'status-shipped';
      case 'processing': return 'status-processing';
      default: return 'status-pending';
    }
  };

  const handleAddAddress = () => {
    // Require zip + state too — without them, USPS/UPS can't deliver and
    // the admin OrderDetailModal shows "City, " with no rest. The
    // pre-2026-05-17 version let users save half-empty addresses.
    if (!newAddress.name || !newAddress.street || !newAddress.city || !newAddress.state || !newAddress.zip) {
      toast.error('Fill in all address fields before saving');
      return;
    }
    // Light ZIP format check for US — backend treats country='USA' as
    // default, so any obvious typo (letters, <5 chars) gets caught early.
    if (newAddress.country === 'USA' && !/^\d{5}(-\d{4})?$/.test(newAddress.zip.trim())) {
      toast.error('US ZIP must be 5 digits (or 5+4)');
      return;
    }
    addShippingAddress(newAddress);
    setShowAddAddress(false);
    setNewAddress({ name: '', street: '', city: '', state: '', zip: '', country: 'USA', isDefault: false });
    toast.success('Address saved');
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      await updateAvatar(file);
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error('Could not update photo — try again');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Cover image must be less than 8MB');
      return;
    }

    setUploadingCover(true);
    try {
      // Reuse the media upload pipeline. We avoid coupling to a non-existent
      // profileAPI.updateCover; the URL is persisted in local state via
      // updateProfile (and to localStorage by the AppContext saveUser helper).
      const { mediaAPI } = await import('../../services/api');
      const result = await mediaAPI.uploadFile(file);
      if (result?.url) {
        updateProfile({ coverImage: result.url });
        toast.success('Cover photo updated');
      }
    } catch (error) {
      console.error('Failed to upload cover:', error);
      toast.error('Cover upload failed — try again');
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-cover-wrap">
          {currentUser.coverImage ? (
            <img
              src={currentUser.coverImage}
              alt=""
              className="profile-cover"
              onError={e => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
                img.parentElement?.classList.add('profile-cover-empty');
              }}
            />
          ) : (
            <div className="profile-cover profile-cover-placeholder" />
          )}
          <div className="profile-cover-overlay" />
          <button
            className="cover-upload-btn"
            onClick={() => coverInputRef.current?.click()}
            disabled={uploadingCover}
            title="Change cover photo"
          >
            {uploadingCover ? (
              <span className="upload-spinner" />
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span>{currentUser.coverImage ? 'Change cover' : 'Add cover'}</span>
              </>
            )}
          </button>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            onChange={handleCoverUpload}
            style={{ display: 'none' }}
          />
        </div>

        <div className="profile-identity">
          <div className="avatar-upload-container">
            <Avatar
              src={currentUser.avatar}
              name={currentUser.displayName}
              size="xl"
              className="profile-avatar"
            />
            <button
              className="avatar-upload-btn"
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              title="Change profile picture"
            >
              {uploadingAvatar ? (
                <span className="upload-spinner" />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              )}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
          </div>
          <div className="profile-name-block">
            <h1>{currentUser.displayName}</h1>
            <p className="profile-email">{currentUser.email}</p>
            {currentUser.isSubscribed && (
              <div className="subscription-badge">
                <span className="badge-tier">{subscriptionTiers.find(t => t.id === currentUser.subscriptionTier)?.name || 'Subscriber'}</span>
                {currentUser.subscriptionExpiry && (
                  <span className="badge-expiry">Renews {new Date(currentUser.subscriptionExpiry).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        <button className={`tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          Orders
        </button>
        <button className={`tab ${activeTab === 'saved' ? 'active' : ''}`} onClick={() => setActiveTab('saved')}>
          Saved
        </button>
        <button className={`tab ${activeTab === 'wishlist' ? 'active' : ''}`} onClick={() => setActiveTab('wishlist')}>
          Wishlist
        </button>
        <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'orders' && (
          <div className="orders-section">
            {userOrders.length === 0 ? (
              <div className="empty-state">
                <p>No orders yet</p>
              </div>
            ) : (
              userOrders.map(order => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div>
                      <span className="order-id">Order #{order.id.slice(-6)}</span>
                      <span className="order-date">{order.createdAt.toLocaleDateString()}</span>
                    </div>
                    <span className={`order-status ${getStatusColor(order.status)}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </div>
                  <div className="order-items">
                    {order.items.map((item, i) => (
                      <div key={i} className="order-item">
                        <img src={item.productImage} alt={item.productName} />
                        <div className="item-info">
                          <span className="item-name">{item.productName}</span>
                          <span className="item-detail">Size: {item.size} × {item.quantity}</span>
                        </div>
                        <span className="item-price">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="order-footer">
                    <span className="order-total">Total: ${order.total.toFixed(2)}</span>
                    {order.trackingNumber && (
                      <span className="tracking">Tracking: {order.trackingNumber}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'saved' && (
          <div className="saved-section">
            {savedPosts.length === 0 ? (
              <div className="empty-state saved-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="saved-empty-icon">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
                </svg>
                <h3>No saved content yet</h3>
                <p>Tap the bookmark icon on any post to save it here for later.</p>
              </div>
            ) : (
              <div className="saved-grid">
                {savedPosts.map(post => (
                  <div key={post.id} className="saved-item" title={post.content || ''}>
                    {(post.mediaUrl || (post.mediaUrls && post.mediaUrls[0])) && (
                      <img
                        src={post.mediaUrl || post.mediaUrls?.[0]}
                        alt=""
                        loading="lazy"
                        onError={e => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                      />
                    )}
                    <button
                      className="saved-download"
                      title="Download"
                      onClick={async () => {
                        const url = post.mediaUrl || post.mediaUrls?.[0];
                        if (!url) return;
                        try {
                          const res = await fetch(url, { credentials: 'omit' });
                          const blob = await res.blob();
                          const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
                          const blobUrl = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = blobUrl;
                          a.download = `onlyjalen-${post.id}.${ext}`;
                          document.body.appendChild(a); a.click(); a.remove();
                          URL.revokeObjectURL(blobUrl);
                        } catch {
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'wishlist' && (
          <div className="wishlist-section">
            {wishlistProducts.length === 0 ? (
              <div className="empty-state"><p>No items in wishlist</p></div>
            ) : (
              <div className="wishlist-grid">
                {wishlistProducts.map(p => (
                  <div key={p.id} className="wishlist-item">
                    <img src={p.images[0]} alt={p.name} />
                    <div className="wishlist-info">
                      <span className="name">{p.name}</span>
                      <span className="price">${(p.salePrice || p.price).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <div className="settings-group">
              <h3>Shipping Addresses</h3>
              {currentUser.shippingAddresses.map(addr => (
                <div key={addr.id} className="address-card">
                  <strong>{addr.name}</strong>
                  <p>{addr.street}</p>
                  <p>{addr.city}, {addr.state} {addr.zip}</p>
                  {addr.isDefault && <span className="default-badge">Default</span>}
                </div>
              ))}
              {!showAddAddress ? (
                <button className="add-address-btn" onClick={() => setShowAddAddress(true)}>
                  + Add New Address
                </button>
              ) : (
                <div className="add-address-form">
                  <input placeholder="Address Name (e.g., Home)" value={newAddress.name} onChange={e => setNewAddress({ ...newAddress, name: e.target.value })} />
                  <input placeholder="Street Address" value={newAddress.street} onChange={e => setNewAddress({ ...newAddress, street: e.target.value })} />
                  <div className="form-row">
                    <input placeholder="City" value={newAddress.city} onChange={e => setNewAddress({ ...newAddress, city: e.target.value })} />
                    <input placeholder="State" value={newAddress.state} onChange={e => setNewAddress({ ...newAddress, state: e.target.value })} />
                  </div>
                  <input placeholder="ZIP Code" value={newAddress.zip} onChange={e => setNewAddress({ ...newAddress, zip: e.target.value })} />
                  <div className="form-actions">
                    <button className="save-btn" onClick={handleAddAddress}>Save Address</button>
                    <button className="cancel-btn" onClick={() => setShowAddAddress(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            <div className="settings-group">
              <h3>Notification Preferences</h3>
              <label className="toggle-option">
                <span>New Posts</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.newPosts} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, newPosts: e.target.checked } })} />
              </label>
              <label className="toggle-option">
                <span>Order Updates</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.orderUpdates} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, orderUpdates: e.target.checked } })} />
              </label>
              <label className="toggle-option">
                <span>Messages</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.messages} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, messages: e.target.checked } })} />
              </label>
              <label className="toggle-option">
                <span>New Products</span>
                <input type="checkbox" checked={currentUser.notificationPrefs.newProducts} onChange={e => updateProfile({ notificationPrefs: { ...currentUser.notificationPrefs, newProducts: e.target.checked } })} />
              </label>
            </div>

            <div className="settings-group">
              <h3>Account</h3>
              {onSignOut && (
                <button className="signout-btn" onClick={onSignOut}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px', marginRight: '8px' }}>
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                    <polyline points="16,17 21,12 16,7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign Out
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
